import { difference, intersection, keyBy, mapValues, uniq } from 'es-toolkit';
import React, { useCallback, useMemo, useState } from 'react';
import {
  CustomSummary,
  CustomTreeData,
  DataTypeProvider,
  Filter,
  FilteringState,
  IntegratedFiltering,
  IntegratedSelection,
  IntegratedSorting,
  SelectionState,
  SortingState,
  SummaryState,
  TreeDataState,
} from '@devexpress/dx-react-grid';
import {
  ColumnChooser,
  Grid,
  TableColumnResizing,
  TableColumnVisibility,
  TableFilterRow,
  TableHeaderRow,
  TableSelection,
  TableSummaryRow,
  TableTreeColumn,
  Toolbar,
  VirtualTable,
} from '@devexpress/dx-react-grid-material-ui';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { Box, Paper, TableCell, Tooltip, Typography } from '@mui/material';

import { expandToFillParentStyles } from '@paragon/client-tools';
import { enqueueWarning, InlineNotification } from '@paragon/notification-tools';
import { assertExhaustiveSwitch, naturalSort, naturalSortByProperty } from '@paragon/utilities';

import { loadComponentsForProductionGroup } from '../../../actions/componentActions';
import {
  BacklogGridRow,
  BacklogGridRowKey,
  getBacklogCheckedRowTotals,
  getBacklogGridRows,
  getBundleGuidFromRowId,
  getBundleRowId,
  getBundlesNotScheduledByProductionGroupGuid,
  getProductionGroupGuidFromRowId,
  getProductionGroupRowId,
  getProjectGuidFromRowId,
  getProjectRowId,
  isBundleRow,
  isGroupedProductionComponentInstanceRow,
  isProductionGroupRow,
  isProjectRow,
} from '../../../selectors/backlogGrid';
import { getDistanceFormatterSelector } from '../../../selectors/getDistanceFormatterSelector';
import { getWarningsByBundleGuid } from '../../../selectors/getWarningsByBundleGuid';
import { LineScheduleGridRow } from '../../../selectors/lineScheduleGrid';
import {
  getProductionGroupHasUnbundledOrUnBatchedComponents,
  getProductionGroupsByProjectGuid,
} from '../../../selectors/productionGroups';
import { checkRows, collapseRows, expandRows, setUserColumnWidths, uncheckRows } from '../../../store/backlogGridSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ComponentPreviewCell } from '../ComponentPreviewCell';
import { CustomTreeCell } from '../CustomTreeCell';
import { ExpandCollapseButtons } from '../ExpandCollapseButtons';
import { GridRoot } from '../GridRoot';
import { Column } from '../gridTypes';
import { distancesToString, lengthFilter, trimmedCaseInsensitiveFilter } from '../gridUtilities';
import { BacklogGridToolbarPlugin } from './BacklogGridToolbarPlugin';

const baseColumnDefinitions: { [columnName: string]: Column<BacklogGridRow> } = {
  expandCollapseByScheduleGroup: {
    name: 'expandCollapseByScheduleGroup',
    title: 'Exp/Col',
    defaultWidth: 70,
    sortingEnabled: false,
    filteringEnabled: false,
  },
  name: {
    name: 'name',
    title: 'Name',
    defaultWidth: 400,
    sortingEnabled: true,
    filteringEnabled: true,
  },
  trussCount: {
    name: 'trussCount',
    title: 'Truss Count',
    sortingEnabled: true,
    filteringEnabled: false,
    defaultWidth: 150,
    align: 'right',
  },
  boardFeet: {
    name: 'boardFeet',
    title: 'BF',
    sortingEnabled: true,
    filteringEnabled: false,
    defaultWidth: 100,
    align: 'right',
    getCellValue: (row) => (row.boardFeet === null ? null : Math.round(row.boardFeet)),
  },
  linearFeet: {
    name: 'linearFeet',
    title: 'LF',
    sortingEnabled: true,
    filteringEnabled: false,
    defaultWidth: 100,
    align: 'right',
    getCellValue: (row) => (row.linearFeet === null ? null : Math.round(row.linearFeet)),
  },
  thicknesses: {
    name: 'thicknesses',
    title: 'Thickness',
    defaultWidth: 110,
    align: 'right',
    sortingEnabled: true,
    sortingCompare: naturalSort,
    filteringEnabled: true,
    filteringPredicate: lengthFilter,
  },
  heights: {
    name: 'heights',
    title: 'Height',
    sortingEnabled: true,
    sortingCompare: naturalSort,
    filteringEnabled: true,
    filteringPredicate: lengthFilter,
    defaultWidth: 90,
    align: 'right',
  },
  widths: {
    name: 'widths',
    title: 'Width',
    defaultWidth: 90,
    align: 'right',
    sortingEnabled: true,
    sortingCompare: naturalSort,
    filteringEnabled: true,
    filteringPredicate: lengthFilter,
  },
  preview: {
    name: 'preview',
    title: 'Preview',
    sortingEnabled: false,
    filteringEnabled: false,
    defaultWidth: 100,
    getCellValue: (row) =>
      row.type === 'groupedProductionComponentInstance' ? (
        <ComponentPreviewCell analysisSetGuid={row.analysisSetGuid} maxWidth={100} maxHeight={25} />
      ) : null,
  },
};

export function BacklogGrid() {
  const dispatch = useAppDispatch();
  const rows = useAppSelector(getBacklogGridRows);
  const productionGroups = useAppSelector((state) => state.productionGroups);
  const productionGroupsByProjectGuid = useAppSelector(getProductionGroupsByProjectGuid);
  const bundles = useAppSelector((state) => state.bundles);
  const bundlesNotScheduledByProductionGroupGuid = useAppSelector(getBundlesNotScheduledByProductionGroupGuid);
  const productionGroupHasUnbundledOrUnbatchedComponents = useAppSelector(
    getProductionGroupHasUnbundledOrUnBatchedComponents,
  );
  const checkedRowIds = useAppSelector((state) => state.backlogGrid.checkedRows);
  const expandedRowIds = useAppSelector((state) => state.backlogGrid.expandedRows);
  const warningsByBundleGuid = useAppSelector(getWarningsByBundleGuid);
  const userColumnWidths = useAppSelector((state) => state.backlogGrid.userColumnWidths);

  const [gridFilters, setGridFilters] = useState<Filter[]>([]);

  const rowsKeyedByRowId = useMemo(() => keyBy(rows, (row) => row.rowId), [rows]);

  const warningShortMessagesByProductionGroupGuid = useMemo(
    () =>
      mapValues(productionGroups, (productionGroup) => {
        const bundlesForProductionGroup = bundlesNotScheduledByProductionGroupGuid[productionGroup.guid] ?? [];
        const notFullyBatchedBundledWarning = productionGroupHasUnbundledOrUnbatchedComponents[productionGroup.guid]
          ? ['Not fully batched/bundled']
          : [];
        const warningsFromBundles = uniq(
          bundlesForProductionGroup.flatMap((bundle) =>
            warningsByBundleGuid[bundle.guid].map((bundleWarning) => bundleWarning.shortMessage),
          ),
        );
        return notFullyBatchedBundledWarning.concat(warningsFromBundles);
      }),
    [
      productionGroups,
      productionGroupHasUnbundledOrUnbatchedComponents,
      bundlesNotScheduledByProductionGroupGuid,
      warningsByBundleGuid,
    ],
  );

  const backlogGridRowsKeyedByRowId = useMemo(() => keyBy(rows, (row) => row.rowId), [rows]);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);

  const { checkedTrussCountTotal, checkedBoardFeetTotal, checkedLinearFeetTotal, heights } =
    useAppSelector(getBacklogCheckedRowTotals);

  function rowSelectionDisabled(row: BacklogGridRow) {
    const { type } = row;
    switch (type) {
      case 'project':
        // Since only one production group can be scheduled at a time, selecting the project level is not useful
        return true;
      case 'productionGroup':
        return productionGroupHasUnbundledOrUnbatchedComponents[row.guid];
      case 'bundle':
        return productionGroupHasUnbundledOrUnbatchedComponents[bundles[row.guid].productionGroupGuid];
      case 'groupedProductionComponentInstance':
        return true;
      default:
        assertExhaustiveSwitch(type);
    }
  }

  const getDescendantRowIds = useCallback(
    (rowId: string): string[] => {
      if (isProjectRow(rowId)) {
        const projectGuid = getProjectGuidFromRowId(rowId);
        const descendantProductionGroupGuids = rows
          .filter((row) => row.type === 'productionGroup' && row.projectGuid === projectGuid)
          .map((row) => row.guid);
        const descendantBundleGuids = rows
          .filter((row) => row.type === 'bundle' && descendantProductionGroupGuids.includes(row.productionGroupGuid))
          .map((row) => row.guid);
        const descendantGroupedProductionComponentInstanceRowIds = rows
          .filter(
            (row) =>
              row.type === 'groupedProductionComponentInstance' && descendantBundleGuids.includes(row.bundleGuid),
          )
          .map((row) => row.rowId);
        return descendantProductionGroupGuids
          .map(getProductionGroupRowId)
          .concat(descendantBundleGuids.map(getBundleRowId))
          .concat(descendantGroupedProductionComponentInstanceRowIds);
      }

      if (isProductionGroupRow(rowId)) {
        const productionGroupGuid = getProductionGroupGuidFromRowId(rowId);
        const descendantBundleGuids = rows
          .filter((row) => row.type === 'bundle' && row.productionGroupGuid === productionGroupGuid)
          .map((row) => row.guid);
        const descendantGroupedProductionComponentInstanceRowIds = rows
          .filter(
            (row) =>
              row.type === 'groupedProductionComponentInstance' && descendantBundleGuids.includes(row.bundleGuid),
          )
          .map((row) => row.rowId);
        return descendantBundleGuids.map(getBundleRowId).concat(descendantGroupedProductionComponentInstanceRowIds);
      }

      if (isBundleRow(rowId)) {
        const bundleGuid = getBundleGuidFromRowId(rowId);
        return rows
          .filter((row) => row.type === 'groupedProductionComponentInstance' && row.bundleGuid === bundleGuid)
          .map((row) => row.rowId);
      }

      return [];
    },
    [rows],
  );

  const getAncestorRowIds = useCallback(
    (rowId: string, rowsKeyedByRowId: { [rowId: string]: BacklogGridRow }): string[] => {
      const row = rowsKeyedByRowId[rowId];
      if (row === undefined) throw new Error('Row not found');
      const { type } = row;
      switch (type) {
        case 'project':
          return [];
        case 'productionGroup': {
          const productionGroupGuid = getProductionGroupGuidFromRowId(rowId);
          const { projectGuid } = productionGroups[productionGroupGuid];
          return [getProjectRowId(projectGuid)];
        }
        case 'bundle': {
          const bundleGuid = getBundleGuidFromRowId(rowId);
          const { productionGroupGuid } = bundles[bundleGuid];
          const { projectGuid } = productionGroups[productionGroupGuid];
          return [getProductionGroupRowId(productionGroupGuid), getProjectRowId(projectGuid)];
        }
        case 'groupedProductionComponentInstance': {
          const { bundleGuid } = row;
          const { productionGroupGuid } = bundles[bundleGuid];
          const { projectGuid } = productionGroups[productionGroupGuid];
          return [
            getBundleRowId(bundleGuid),
            getProductionGroupRowId(productionGroupGuid),
            getProjectRowId(projectGuid),
          ];
        }
        default:
          assertExhaustiveSwitch(type);
      }
    },
    [bundles, productionGroups],
  );

  /** This function exists to determine ancestor rows that should be de/selected. They should be de/selected if all
   * siblings are selected. */
  function getAffectedAncestorRowIds(rowId: string): string[] {
    function getAffectedAncestorsForProductionGroupRowOrEmptyArray(productionGroupGuid: string) {
      const { projectGuid } = productionGroups[productionGroupGuid];
      const siblingRowIds = productionGroupsByProjectGuid[projectGuid]
        .filter((productionGroup) => productionGroup.guid !== productionGroupGuid)
        .map((productionGroup) => getProductionGroupRowId(productionGroup.guid));
      if (siblingRowIds.every((siblingRowId) => checkedRowIds.includes(siblingRowId))) {
        return [getProjectRowId(projectGuid)];
      }
      return [];
    }

    function getAffectedAncestorsForBundleRowOrEmptyArray(bundleGuid: string) {
      const { productionGroupGuid } = bundles[bundleGuid];
      const siblingRowIds = bundlesNotScheduledByProductionGroupGuid[productionGroupGuid]
        .filter((bundle) => bundle.guid !== bundleGuid)
        .map((bundle) => getBundleRowId(bundle.guid));
      if (siblingRowIds.every((siblingRowId) => checkedRowIds.includes(siblingRowId))) {
        return [getProductionGroupRowId(productionGroupGuid)].concat(
          getAffectedAncestorsForProductionGroupRowOrEmptyArray(productionGroupGuid),
        );
      }
      return [];
    }

    if (isProductionGroupRow(rowId)) {
      const productionGroupGuid = getProductionGroupGuidFromRowId(rowId);
      return getAffectedAncestorsForProductionGroupRowOrEmptyArray(productionGroupGuid);
    }

    if (isBundleRow(rowId)) {
      const bundleGuid = getBundleGuidFromRowId(rowId);
      return getAffectedAncestorsForBundleRowOrEmptyArray(bundleGuid);
    }

    if (isGroupedProductionComponentInstanceRow(rowId)) {
      const groupedProductionComponentInstanceRow = rows.find((row) => row.rowId === rowId);
      if (
        !groupedProductionComponentInstanceRow ||
        groupedProductionComponentInstanceRow.type !== 'groupedProductionComponentInstance'
      )
        throw new Error('Row not found');
      const { bundleGuid } = groupedProductionComponentInstanceRow;
      const siblingRowIds = rows
        .filter(
          (row) =>
            row.type === 'groupedProductionComponentInstance' && row.bundleGuid === bundleGuid && row.rowId !== rowId,
        )
        .map((row) => row.rowId);
      if (siblingRowIds.every((siblingRowId) => checkedRowIds.includes(siblingRowId))) {
        return [getBundleRowId(bundleGuid)].concat(getAffectedAncestorsForBundleRowOrEmptyArray(bundleGuid));
      }
      return [];
    }

    return [];
  }

  function rowSelectionIndeterminate(checkedRows: readonly string[]) {
    return function rowSelectionIndeterminateWithCheckedRows(row: LineScheduleGridRow): boolean {
      const { rowId, type } = row;
      if (checkedRows.includes(rowId) || type === 'groupedProductionComponentInstance') return false;

      const descendantRowIds = getDescendantRowIds(rowId);
      const numberOfCheckedDescendantRowIds = intersection(checkedRows, descendantRowIds).length;
      return numberOfCheckedDescendantRowIds > 0 && numberOfCheckedDescendantRowIds < descendantRowIds.length;
    };
  }

  const columns: readonly Column<BacklogGridRow>[] = useMemo(() => {
    function trimmedCaseInsensitiveFilterForSelfAndHierarchyWithValueGetter(valueGetter: (row: BacklogGridRow) => any) {
      return function trimmedCaseInsensitiveFilterForSelfAndHierarchy(value: any, filter: Filter, row: BacklogGridRow) {
        const descendantRows = getDescendantRowIds(row.rowId).map((rowId) => rowsKeyedByRowId[rowId]);
        if (descendantRows.some((descendantRow) => trimmedCaseInsensitiveFilter(valueGetter(descendantRow), filter)))
          return true;

        const ancestorRows = getAncestorRowIds(row.rowId, rowsKeyedByRowId);
        if (
          ancestorRows.some((ancestorRowId) =>
            trimmedCaseInsensitiveFilter(valueGetter(rowsKeyedByRowId[ancestorRowId]), filter),
          )
        )
          return true;

        return trimmedCaseInsensitiveFilter(valueGetter(row), filter);
      };
    }

    return [
      {
        ...baseColumnDefinitions['expandCollapseByScheduleGroup'],
        getCellValue: (row) => {
          if (row.type !== 'productionGroup' || productionGroupHasUnbundledOrUnbatchedComponents[row.guid]) return null;
          const expandableDescendantRowIds = getDescendantRowIds(row.rowId);
          return (
            <ExpandCollapseButtons
              rowId={row.rowId}
              expandRows={(rowIds) => {
                dispatch(expandRows(rowIds));
                dispatch(loadComponentsForProductionGroup(row.guid));
              }}
              collapseRows={(rowIds) => void dispatch(collapseRows(rowIds))}
              expandableDescendantRowIds={expandableDescendantRowIds}
              expandedRowIds={expandedRowIds}
            />
          );
        },
      },
      {
        ...baseColumnDefinitions['name'],
        filteringPredicate: trimmedCaseInsensitiveFilterForSelfAndHierarchyWithValueGetter((row) => row.name),
      },
      baseColumnDefinitions['trussCount'],
      baseColumnDefinitions['boardFeet'],
      baseColumnDefinitions['linearFeet'],
      {
        ...baseColumnDefinitions['thicknesses'],
        getCellValue: (row) => distancesToString(row.thicknesses, distanceFormatter),
      },
      {
        ...baseColumnDefinitions['heights'],
        getCellValue: (row) => distancesToString(row.heights, distanceFormatter),
      },
      {
        ...baseColumnDefinitions['widths'],
        getCellValue: (row) => distancesToString(row.widths, distanceFormatter),
      },
      baseColumnDefinitions['preview'],
    ];
  }, [
    getAncestorRowIds,
    getDescendantRowIds,
    rowsKeyedByRowId,
    distanceFormatter,
    dispatch,
    expandedRowIds,
    productionGroupHasUnbundledOrUnbatchedComponents,
  ]);
  const keyedColumns = useMemo(() => keyBy(columns, (column) => column.name), [columns]);
  const columnSortingExtensions = useMemo(
    () =>
      columns.map((column) => ({
        columnName: column.name,
        sortingEnabled: column.sortingEnabled,
        compare: column.sortingCompare,
      })),
    [columns],
  );
  const columnFilteringExtensions = useMemo(
    () =>
      columns.map((column) => ({
        columnName: column.name,
        filteringEnabled: column.filteringEnabled,
        predicate: column.filteringPredicate,
      })),
    [columns],
  );

  const getFilteredRows = useCallback(
    (filters: readonly Filter[]) =>
      rows.filter((row) =>
        filters.every((filter) => {
          const column = keyedColumns[filter.columnName];
          if (!column.filteringPredicate) return true;
          return column.filteringPredicate(row[column.name as BacklogGridRowKey], filter, row);
        }),
      ),
    [rows, keyedColumns],
  );

  function onFilter(filters: Filter[]) {
    const filteredRows = getFilteredRows(gridFilters);
    const newFilteredRows = getFilteredRows(filters);
    const newlyHiddenCheckedRows = filteredRows.filter(
      (row) => !newFilteredRows.includes(row) && checkedRowIds.includes(row.rowId) && !rowSelectionDisabled(row),
    );
    if (newlyHiddenCheckedRows.length > 0) {
      dispatch(enqueueWarning('Some selected rows are now hidden'));
    }

    setGridFilters(filters);
  }

  if (rows.length === 0)
    return (
      <Typography>
        No backlog to list. Create production groups, batches, and bundles in the Batches & Bundles tab.
      </Typography>
    );

  function onExpandedRowsChange(newExpandedRows: (string | number)[]) {
    for (const newlyCollapsedRow of difference(expandedRowIds, newExpandedRows)) {
      dispatch(collapseRows([newlyCollapsedRow.toString()]));
    }

    for (const newlyExpandedRow of difference(newExpandedRows, expandedRowIds)) {
      const newlyExpandedRowString = newlyExpandedRow.toString();
      if (isProductionGroupRow(newlyExpandedRowString)) {
        dispatch(loadComponentsForProductionGroup(getProductionGroupGuidFromRowId(newlyExpandedRowString)));
      }
      dispatch(expandRows([newlyExpandedRowString]));
    }
  }

  function onCheckedRowsChange(newCheckedRows: (string | number)[]) {
    for (const uncheckedRowStringOrNumber of difference(checkedRowIds, newCheckedRows)) {
      const uncheckedRowId = uncheckedRowStringOrNumber.toString();

      if (rowSelectionDisabled(backlogGridRowsKeyedByRowId[uncheckedRowId])) continue;

      dispatch(
        uncheckRows(
          [uncheckedRowId]
            .concat(getDescendantRowIds(uncheckedRowId))
            .concat(getAffectedAncestorRowIds(uncheckedRowId)),
        ),
      );
    }

    for (const newlyCheckedRowStringOrNumber of difference(newCheckedRows, checkedRowIds)) {
      const newlyCheckedRowId = newlyCheckedRowStringOrNumber.toString();

      if (rowSelectionDisabled(backlogGridRowsKeyedByRowId[newlyCheckedRowId])) continue;

      const descendantRowIds = getDescendantRowIds(newlyCheckedRowId);
      const filteredRowIds = getFilteredRows(gridFilters).map((row) => row.rowId);
      const visibleDescendantRowIds = descendantRowIds.filter(
        (rowId) => filteredRowIds.includes(rowId) || rowSelectionDisabled(rowsKeyedByRowId[rowId]),
      );
      // When bundles are being checked, we always want to include all of their children, even if some are hidden by a
      // filter.
      const invisibleBundleDescendantRowIds = [newlyCheckedRowId]
        .concat(visibleDescendantRowIds)
        .filter((rowId) => rowsKeyedByRowId[rowId].type === 'bundle')
        .flatMap((rowId) => getDescendantRowIds(rowId))
        .filter((rowId) => !visibleDescendantRowIds.includes(rowId));

      // In the case that the user has selected the parent to select all the visible children, the parent is not
      // actually checked, but shown as indeterminate. The perceived intention of selecting the parent again is to
      // deselect all the visible children. This block handles this special case.
      const allVisibleDescendantRowsChecked = visibleDescendantRowIds.every((row) => checkedRowIds.includes(row));
      if (allVisibleDescendantRowsChecked) {
        dispatch(uncheckRows(visibleDescendantRowIds.concat(invisibleBundleDescendantRowIds)));
        return;
      }

      const affectedAncestorRowIds = getAffectedAncestorRowIds(newlyCheckedRowId);
      const rowIdsToCheck = visibleDescendantRowIds
        .concat(invisibleBundleDescendantRowIds)
        .concat(affectedAncestorRowIds);
      const checkedRowIdsWithNewlyCheckedRowIds = checkedRowIds.concat(rowIdsToCheck);
      const allDescendantRowsChecked = descendantRowIds.every((rowId) =>
        checkedRowIdsWithNewlyCheckedRowIds.includes(rowId),
      );
      if (allDescendantRowsChecked) rowIdsToCheck.push(newlyCheckedRowId);
      dispatch(checkRows(rowIdsToCheck));
    }
  }

  return (
    <Paper sx={{ flex: 2, ...expandToFillParentStyles.containerStyleProps }}>
      <Grid rows={rows} columns={columns} getRowId={(row) => row.rowId} rootComponent={GridRoot}>
        <DataTypeProvider
          for={['name']}
          formatterComponent={({ row }) => {
            const { type, guid, name } = row as BacklogGridRow;
            switch (type) {
              case 'project':
                return <span>{name}</span>;
              case 'productionGroup':
                return warningShortMessagesByProductionGroupGuid[guid].length > 0 ? (
                  <Box style={{ display: 'flex', alignItems: 'center' }}>
                    <ReportProblemIcon sx={{ mr: 1 }} />
                    <span>{name}</span>
                    {naturalSortByProperty(warningShortMessagesByProductionGroupGuid[guid], (message) => message).map(
                      (warningShortMessage) => (
                        <InlineNotification sx={{ my: 0, ml: 1 }} type="error" key={warningShortMessage}>
                          {warningShortMessage}
                        </InlineNotification>
                      ),
                    )}
                  </Box>
                ) : (
                  <span>{name}</span>
                );
              case 'bundle':
                return warningsByBundleGuid[guid].length > 0 ? (
                  <Box style={{ display: 'flex', alignItems: 'center' }}>
                    <span>{name}</span>
                    {naturalSortByProperty(warningsByBundleGuid[guid], (warning) => warning.shortMessage).map(
                      (warning) => (
                        <Tooltip
                          disableInteractive
                          sx={{ ml: 1 }}
                          title={warning.longMessage}
                          key={warning.longMessage}
                        >
                          <span>
                            <InlineNotification sx={{ my: 0, ml: 1 }} type="error">
                              {warning.shortMessage}
                            </InlineNotification>
                          </span>
                        </Tooltip>
                      ),
                    )}
                  </Box>
                ) : (
                  <span>{name}</span>
                );
              case 'groupedProductionComponentInstance':
                return <span>{name}</span>;
              default:
                assertExhaustiveSwitch(type);
            }
          }}
        />

        <TreeDataState expandedRowIds={expandedRowIds} onExpandedRowIdsChange={onExpandedRowsChange} />

        <SortingState
          defaultSorting={[
            { columnName: 'targetDate', direction: 'asc' },
            { columnName: 'thickness', direction: 'asc' },
            { columnName: 'name', direction: 'asc' },
          ]}
          columnExtensions={columnSortingExtensions}
        />
        <IntegratedSorting columnExtensions={columnSortingExtensions} />

        <FilteringState filters={gridFilters} onFiltersChange={onFilter} columnExtensions={columnFilteringExtensions} />
        <IntegratedFiltering columnExtensions={columnFilteringExtensions} />

        <SelectionState selection={checkedRowIds} onSelectionChange={onCheckedRowsChange} />
        <IntegratedSelection />

        <CustomTreeData
          getChildRows={(currentRow, rootRows) => {
            if (!currentRow) {
              return naturalSortByProperty(
                rootRows.filter((row) => row.type === 'project'),
                (projectRow) => projectRow.name,
              );
            }
            if (currentRow.type === 'project') {
              const childRows = naturalSortByProperty(
                rootRows.filter((row) =>
                  row.type === 'productionGroup' ? row.projectGuid === currentRow.guid : false,
                ),
                (productionGroupRow) => productionGroupRow.name,
              );
              return childRows.length > 0 ? childRows : null;
            }
            if (currentRow.type === 'productionGroup') {
              const childRows = naturalSortByProperty(
                rootRows.filter((row) => (row.type === 'bundle' ? row.productionGroupGuid === currentRow.guid : false)),
                (bundleRow) => bundleRow.name,
              );
              return childRows.length > 0 ? childRows : null;
            }
            if (currentRow.type === 'bundle') {
              const childRows = naturalSortByProperty(
                rootRows.filter(
                  (row) => row.type === 'groupedProductionComponentInstance' && row.bundleGuid === currentRow.guid,
                ),
                (groupedProductionComponentInstanceRow) => groupedProductionComponentInstanceRow.name,
              );
              return childRows.length > 0 ? childRows : null;
            }
            return null;
          }}
        />

        <SummaryState
          totalItems={[
            { columnName: 'trussCount', type: '' },
            { columnName: 'boardFeet', type: '' },
            { columnName: 'linearFeet', type: '' },
            { columnName: 'height', type: '' },
          ]}
        />
        <CustomSummary totalValues={[checkedTrussCountTotal, checkedBoardFeetTotal, checkedLinearFeetTotal, heights]} />

        <VirtualTable columnExtensions={columns.map((column) => ({ columnName: column.name, align: column.align }))} />

        <TableColumnResizing
          columnWidths={
            userColumnWidths ?? columns.map((column) => ({ columnName: column.name, width: column.defaultWidth }))
          }
          onColumnWidthsChange={(newUserColumnWidths) => void dispatch(setUserColumnWidths(newUserColumnWidths))}
        />
        <TableHeaderRow />
        <TableFilterRow cellComponent={TableFilterCell} />
        <TableColumnVisibility />
        <Toolbar />
        <BacklogGridToolbarPlugin />
        <ColumnChooser />
        <TableTreeColumn
          for="name"
          showSelectionControls
          cellComponent={(props) => (
            <CustomTreeCell
              rowSelectionDisabled={rowSelectionDisabled}
              rowSelectionHidden={(row) => row.type === 'project' || row.type === 'groupedProductionComponentInstance'}
              rowSelectionIndeterminate={rowSelectionIndeterminate(checkedRowIds)}
              {...props}
            />
          )}
        />
        {checkedRowIds.length > 0 && <TableSummaryRow itemComponent={TableSummaryRowCell} />}
        <TableSelection selectByRowClick showSelectionColumn={false} />
      </Grid>
    </Paper>
  );
}

const TableSummaryRowCell = ({ value }: TableSummaryRow.ItemProps) => <Typography>{value}</Typography>;

const TableFilterCell = (props: TableFilterRow.CellProps) =>
  baseColumnDefinitions[props.column.name].filteringEnabled ? <TableFilterRow.Cell {...props} /> : <TableCell />;
